import React, { useCallback, useEffect, useState } from "react";
import { AppLayout } from "../components/app/AppLayout";
import { FacilityDrawer } from "../components/app/FacilityDrawer";
import { StatusPill } from "../components/app/StatusPill";
import { watchlistApi } from "../lib/api";
import { Star, Loader2, MapPin, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { getApiError } from "../lib/utils";

export default function Watchlist() {
  const [state, setState] = useState({ loading: true, items: [] });
  const [drawerId, setDrawerId] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await watchlistApi.list();
      setState({ loading: false, items: r.items || [] });
    } catch (e) {
      toast.error(getApiError(e, "Could not load watchlist"));
      setState({ loading: false, items: [] });
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshTick]);

  const unstar = async (fid, e) => {
    e?.stopPropagation?.();
    try {
      await watchlistApi.unstar(fid);
      setState((s) => ({ ...s, items: s.items.filter((f) => f.facility_id !== fid) }));
    } catch (err) {
      toast.error(getApiError(err, "Could not unstar"));
    }
  };

  return (
    <AppLayout>
      <div data-testid="watchlist-page" className="mx-auto max-w-5xl">
        <div className="mb-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-nmi-700">
            Personal
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900 md:text-4xl">
            My Watchlist
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Star facilities you want a heads-up on. Every morning at <b>7am GMT</b> we email
            + Telegram you a digest of any suspend/reactivate events on your starred list.
          </p>
        </div>

        {state.loading ? (
          <div className="flex items-center gap-2 text-slate-500 p-8">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : state.items.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-slate-200 bg-white p-12 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
              <Star size={26} className="text-amber-500" />
            </div>
            <h2 className="font-heading text-lg font-bold text-slate-900">Your watchlist is empty</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
              Open any facility from the Facilities page and tap the <b>star</b> in the drawer
              header to add it here.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
              {state.items.length} facilit{state.items.length === 1 ? "y" : "ies"} watched
            </div>
            <ul className="divide-y divide-slate-100">
              {state.items.map((f) => (
                <li
                  key={f.facility_id}
                  data-testid={`watchlist-row-${f.facility_id}`}
                  onClick={() => setDrawerId(f.facility_id)}
                  className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-nmi-50"
                >
                  <button
                    onClick={(e) => unstar(f.facility_id, e)}
                    data-testid={`watchlist-unstar-${f.facility_id}`}
                    className="shrink-0 rounded-md p-1.5 text-amber-500 hover:bg-amber-50"
                    title="Remove from watchlist"
                    aria-label="Unstar facility"
                  >
                    <Star size={18} fill="currentColor" strokeWidth={0} />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-semibold text-slate-900">{f.name}</span>
                      <StatusPill status={f.status} />
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                      <span className="font-bold uppercase tracking-wider">{f.category}</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={11} /> {f.town || "—"}{f.region ? `, ${f.region}` : ""}
                      </span>
                      {f.phones?.[0] && (<><span>·</span><span>{f.phones[0]}</span></>)}
                    </div>
                  </div>
                  <ChevronRight size={16} className="shrink-0 text-slate-400" />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {drawerId && (
        <FacilityDrawer
          facilityId={drawerId}
          onClose={() => { setDrawerId(null); setRefreshTick((t) => t + 1); }}
        />
      )}
    </AppLayout>
  );
}
